import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, MapPin, Users, Newspaper, Tag as TagI, Feather, Video,
  BarChart2, Calendar, Map, Cross, DollarSign, LayoutGrid, FolderOpen,
  Phone, LifeBuoy, Settings as Cog, Tv, ListChecks, ExternalLink, FileText, Bell, Mail,
  Layout as LayoutI, BarChart3, Share2,
  Menu as MenuIcon, X,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/iller", label: "İl Yönetimi", icon: MapPin, super: true },
  { to: "/admin/kullanicilar", label: "Kullanıcılar & Rol", icon: Users, super: true },
  { to: "/admin/haberler", label: "Haberler", icon: Newspaper },
  { to: "/admin/kategoriler", label: "Kategori & Tag", icon: TagI },
  { to: "/admin/yazarlar", label: "Köşe Yazarları", icon: Feather },
  { to: "/admin/videolar", label: "Videolar", icon: Video },
  { to: "/admin/anketler", label: "Anketler", icon: BarChart2 },
  { to: "/admin/etkinlikler", label: "Etkinlikler", icon: Calendar },
  { to: "/admin/sehir-rehberi", label: "Şehir Rehberi", icon: Map },
  { to: "/admin/eczaneler", label: "Eczaneler", icon: Cross },
  { to: "/admin/reklamlar", label: "Reklamlar", icon: DollarSign },
  { to: "/admin/anasayfa", label: "Anasayfa Blokları", icon: LayoutGrid },
  { to: "/admin/medya", label: "Dosya Havuzu", icon: FolderOpen },
  { to: "/admin/haber-hatti", label: "Haber Hattı", icon: Phone },
  { to: "/admin/destek", label: "Destek", icon: LifeBuoy },
  { to: "/admin/tv", label: "TV & Canlı Yayın", icon: Tv },
  { to: "/admin/mail", label: "Mail & Şablonlar", icon: Mail, super: true },
  { to: "/admin/bildirimler", label: "Push Bildirim", icon: Bell },
  { to: "/admin/paylasim", label: "Sosyal Paylaşım", icon: Share2 },
  { to: "/admin/sayfalar", label: "Footer & Sayfalar", icon: LayoutI },
  { to: "/admin/analitik", label: "Analitik", icon: BarChart3 },
  { to: "/admin/ayarlar", label: "Ayarlar", icon: Cog },
  { to: "/admin/audit", label: "Audit Log", icon: ListChecks, super: true },
];

export default function AdminLayout() {
  const { user, isAdmin, isSuperAdmin, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false); // Mobile sidebar open state

  // Auto-close on route change (mobile)
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) return <div className="p-6">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace />;
  if (!isAdmin) return <div className="p-6">Bu sayfaya erişim yetkiniz yok.</div>;

  const visibleLinks = LINKS.filter((l) => !l.super || isSuperAdmin);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#0b0b0d] text-white flex items-center justify-between px-3 h-14 shadow">
        <button onClick={() => setOpen(true)} className="p-2 hover:bg-white/10 rounded" aria-label="Menu">
          <MenuIcon className="w-5 h-5" />
        </button>
        <Logo size="sm" invert />
        <Link to="/" className="p-2 hover:bg-white/10 rounded" title="Siteye dön" aria-label="Site">
          <ExternalLink className="w-4 h-4" />
        </Link>
      </div>

      {/* Backdrop (mobile only when open) */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-50 lg:z-auto
        w-56 h-screen bg-[#0b0b0d] text-white
        overflow-y-auto overflow-x-hidden
        transform transition-transform duration-200 ease-out
        ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        flex-shrink-0
      `}>
        <div className="px-3 py-3 border-b border-white/10 flex items-center justify-between">
          <Logo size="sm" invert />
          <div className="flex items-center gap-1">
            <Link to="/" className="hidden lg:inline-flex p-1.5 text-gray-300 hover:text-white hover:bg-white/5 rounded" title="Siteye dön">
              <ExternalLink className="w-4 h-4" />
            </Link>
            <button onClick={() => setOpen(false)} className="lg:hidden p-1.5 hover:bg-white/10 rounded" aria-label="Kapat">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="py-1.5">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-white/5 ${isActive ? "bg-[#e11d2a] text-white" : "text-gray-200"}`}
            >
              <l.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 mt-2 border-t border-white/10 text-[11px] text-gray-400">
          {user.email}<br/>
          <span className="text-gray-500">{user.role}</span>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 p-4 md:p-6 pt-16 lg:pt-6">
        <Outlet />
      </main>
    </div>
  );
}
